/** @format */
import React, { useState } from 'react';
import {
  Grid,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TableContainer,
  Box,
  LinearProgress,
} from '@mui/material';
import { useBusiness } from 'hooks';
import { BusinessList, SimpleDialog, InputV2, Icons, PriceLabel } from 'components';
import { useFieldArray, useForm, useFormContext } from 'react-hook-form';
import createToast from 'components/Toast/Toast';
import {useCurrencyUnit} from 'hooks';

import clsx from 'clsx';
import NumberFormat from 'react-number-format';

import { useStyles } from './Style';

const Beneficiary = (props) => {
  const classes = useStyles();
  const currencyText = useCurrencyUnit(false);
  const methods = useFormContext();
  const businessInfo = useBusiness();

  const [editRowSelected, setEditRowSelected] = useState(-1);
  const { register, formState: { errors }, trigger, control, getValues, reset } = useForm({
    mode: 'onChange',
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [indexSelected, setIndexSelected] = useState(-1);
  const [mySharePercent, setMySharePercent] = useState(0);
  const [myDiscountSharePercent, setMyDiscountSharePercent] = useState(0);
  const [enterPercent, setEnterPercent] = useState(true);

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: 'scStakeholderList',
  });

  const removeRow = (index) => {
    setIndexSelected(index);
    setOpenDialog(true);
  };

  const confirmRemove = () => {
    const rowValues = fields[indexSelected];

    if (rowValues.percentage) {
      setMySharePercent((val) => val - rowValues.percentage);
    }
    if (rowValues.discountSharePercent) {
      setMyDiscountSharePercent((val) => val - rowValues.discountSharePercent);
    }

    setOpenDialog(false);
    remove(indexSelected);
    setIndexSelected(-1);
  };

  const closeDialog = () => {
    setOpenDialog(false);
  };

  const addRow = async () => {
    try {
      const valid = await trigger([
        'rowBusinessId',
        'rowFixedShareAmount',
        'rowPercentage',
        'rowDiscountSharePercent',
      ]);

      const ownerShare = {
        percentShare: 100,
        discountSharePercent: 100,
      };

      const rowPercentage = parseFloat(getValues('rowPercentage'));
      const rowDiscountSharePercent = parseFloat(getValues('rowDiscountSharePercent'));

      if (rowPercentage) {
        if (mySharePercent + rowPercentage > 100) {
          createToast('خطا', 'جمع درصدهای تسهیم بیشتر از 100 درصد', 'danger');

          return;
        }
        setEnterPercent(true);
        ownerShare.percentShare -= mySharePercent + rowPercentage;
        setMySharePercent((val) => val + rowPercentage);
      }

      if (rowDiscountSharePercent) {
        if (myDiscountSharePercent + rowDiscountSharePercent > 100) {
          createToast('خطا', 'جمع درصدهای تخفیف بیشتر از 100 درصد', 'danger');

          return;
        }
        setEnterPercent(true);
        ownerShare.discountSharePercent -= myDiscountSharePercent + rowDiscountSharePercent;
        setMyDiscountSharePercent((val) => val + rowDiscountSharePercent);
      }

      if (valid) {
        const rowValues = {
          businessId: getValues('rowBusinessId'),
          businessName: getValues('rowBusinessId'),
          fixedShareAmount: getValues('rowFixedShareAmount'),
          percentage: getValues('rowPercentage'),
          discountSharePercent: getValues('rowDiscountSharePercent'),
        };

        append(rowValues);
        reset({});
      }
    } catch (e) {
      console.log('ee :', e);
    }
  };

  const editRow = (index) => {
    setEditRowSelected(index);
  };

  const saveRow = (index, rowData) => {
    const data = methods.getValues().scStakeholderList[index];

    if (data.discountSharePercent) {
      const discount =
        parseFloat(data.discountSharePercent) - parseFloat(rowData.discountSharePercent || 0);
      setMyDiscountSharePercent((prev) => prev + discount);
    }

    if (data.percentage) {
      const percentage = parseFloat(data.percentage) - parseFloat(rowData.percentage || 0);
      setMySharePercent((prev) => prev + percentage);
    }

    fields[index] = { ...rowData, ...data };
    setEditRowSelected(-1);
  };

  const ActionsBtn = () => {
    return (
      <>
        <Button onClick={confirmRemove} variant="contained" color="primary">
          تایید
        </Button>
        <Button onClick={closeDialog} variant="contained">
          انصراف
        </Button>
      </>
    );
  };

  return (
    <>
      <SimpleDialog
        title="حذف ذینفع"
        content="آیا از حذف اطمینان دارید؟"
        open={openDialog}
        actions={<ActionsBtn />}
      />
      <Grid container direction="row" alignItems="center">
        {/* This block will be ignored when submitting the form. */}
        <Grid container item xs={12} spacing={3} alignItems="flex-start">
          {/* نام کسب و کار */}
          <Grid item xs={3} style={{ marginTop: 10 }}>
            <BusinessList name="rowBusinessId" errors={errors} control={control} required />
          </Grid>
          <Grid item xs={3}>
            <InputV2
              label="مبلغ تسهیم"
              errors={errors}
              name="rowFixedShareAmount"
              register={register}
              rules={['required', 'isNumeric']}
              fullWidth
              control={control}
              currency
              hasMargin={false}
              defaultValue=""
            />
          </Grid>
          <Grid item xs={3}>
            <InputV2
              label="درصد تسهیم"
              errors={errors}
              name="rowPercentage"
              register={register}
              fullWidth
              control={control}
              currency
              hasMargin={false}
              currencyText="%"
            />
          </Grid>
          <Grid item xs={2}>
            <InputV2
              label="درصد تخفیف"
              errors={errors}
              name="rowDiscountSharePercent"
              register={register}
              tooltip="سهم درصدی این کسب‌وکار در تخفیف"
              fullWidth
              control={control}
              currency
              hasMargin={false}
              currencyText="%"
            />
          </Grid>
          <Grid item xs={1}>
            <Button color="primary" variant="outlined" onClick={addRow} style={{ marginTop: 30 }}>
              افزودن
            </Button>
          </Grid>
        </Grid>

        {fields.length > 0 && (
          <Grid item xs={12}>
            <Box mt={3}>
              <TableContainer component={Paper} className={classes.table}>
                <Table>
                  <TableHead style={{ backgroundColor: '#eef1f5' }}>
                    <TableRow>
                      <TableCell align="center">شناسه کسب‌وکار</TableCell>
                      <TableCell align="center">نام کسب‌وکار</TableCell>
                      <TableCell align="center">
                        مبلغ تسهیم <small>({currencyText})</small>
                      </TableCell>
                      <TableCell align="center">
                        درصد تسهیم
                        {mySharePercent > 0 && (
                          <LinearProgress variant="determinate" value={mySharePercent} />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        درصد تخفیف
                        {myDiscountSharePercent > 0 && (
                          <LinearProgress variant="determinate" value={myDiscountSharePercent} />
                        )}
                      </TableCell>
                      <TableCell align="center">عملیات</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {enterPercent && (
                      <TableRow className={classes.exceptionRow}>
                        <TableCell align="center">{businessInfo.id}</TableCell>
                        <TableCell>{businessInfo.name}</TableCell>
                        <TableCell align="center">-</TableCell>
                        <TableCell align="center">{100 - mySharePercent} %</TableCell>
                        <TableCell align="center">{100 - myDiscountSharePercent} %</TableCell>
                        <TableCell align="center" />
                      </TableRow>
                    )}
                    {fields.map((row, index) => (
                      <TableRow key={row.id}>
                        <TableCell align="center">{row.businessName.id}</TableCell>
                        <TableCell style={{ width: 280 }}>
                          <span
                            className={clsx({
                              [classes.hiddenRow]: editRowSelected === index,
                            })}
                          >
                            {row.businessName.name}
                          </span>
                          <span
                            className={clsx({
                              [classes.hiddenRow]: editRowSelected !== index,
                            })}
                          >
                            <BusinessList
                              name={`scStakeholderList[${index}].businessId`}
                              errors={methods.formState.errors}
                              control={methods.control}
                              defaultValue={row.businessId}
                            />
                          </span>
                        </TableCell>
                        <TableCell align="center">
                          <span
                            className={clsx({
                              [classes.hiddenRow]: editRowSelected === index,
                            })}
                          >
                            <PriceLabel amount={row?.fixedShareAmount} />
                          </span>
                          <span
                            className={clsx({
                              [classes.hiddenRow]: editRowSelected !== index,
                            })}
                          >
                            <InputV2
                              errors={methods.formState.errors}
                              name={`scStakeholderList[${index}].fixedShareAmount`}
                              register={methods.register}
                              rules={['required']}
                              fullWidth
                              control={methods.control}
                              defaultValue={row.fixedShareAmount}
                              currency
                            />
                          </span>
                        </TableCell>
                        <TableCell align="center">
                          <span
                            className={clsx({
                              [classes.hiddenRow]: editRowSelected === index,
                            })}
                          >
                            {`${row.percentage} %`}
                          </span>
                          <span
                            className={clsx({
                              [classes.hiddenRow]: editRowSelected !== index,
                            })}
                          >
                            <InputV2
                              errors={methods.formState.errors}
                              name={`scStakeholderList[${index}].percentage`}
                              register={methods.register}
                              rules={['required']}
                              fullWidth
                              control={methods.control}
                              defaultValue={row.percentage}
                            />
                          </span>
                        </TableCell>
                        <TableCell align="center">
                          <span
                            className={clsx({
                              [classes.hiddenRow]: editRowSelected === index,
                            })}
                          >
                            {`${row.discountSharePercent} %`}
                          </span>
                          <span
                            className={clsx({
                              [classes.hiddenRow]: editRowSelected !== index,
                            })}
                          >
                            <InputV2
                              errors={methods.formState.errors}
                              name={`scStakeholderList[${index}].discountSharePercent`}
                              register={methods.register}
                              fullWidth
                              control={methods.control}
                              defaultValue={row.discountSharePercent}
                            />
                          </span>
                        </TableCell>
                        <TableCell align="right" style={{ width: 150 }}>
                          <>
                            {editRowSelected === index ? (
                              <IconButton
                                variant="contained"
                                color="primary"
                                onClick={() => saveRow(index, row)}
                                size="large">
                                <Icons icon='save' color='#2B3990' size='25' />

                              </IconButton>
                            ) : (
                              <IconButton
                                variant="contained"
                                color="primary"
                                onClick={() => editRow(index)}
                                size="large">
                                <Icons icon='editPen' color='#2B3990' size='20' />


                              </IconButton>
                            )}

                            <IconButton
                              variant="contained"
                              color="primary"
                              aria-label="delete"
                              onClick={() => removeRow(index)}
                              size="large">
                              <Icons icon='Trash' color='#2B3990' size='23' />

                            </IconButton>
                          </>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default Beneficiary;
